import { useCallback, useMemo } from 'react';
import ReactFlow, {
  type Node,
  type Edge,
  type ReactFlowInstance,
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  ConnectionMode,
  ReactFlowProvider,
} from 'reactflow';
import 'reactflow/dist/style.css';
import type { OrganogramNode } from '../types';
import OrganogramNodeCard from './OrganogramNodeCard';

// Stable nodeTypes/edgeTypes references (React Flow warns if these change between renders)
const NODE_TYPES = {
  organogramNode: OrganogramNodeCard,
};
const EDGE_TYPES: Record<string, never> = {};

interface OrganogramFlowProps {
  nodes: OrganogramNode[];
  onNodeClick: (nodeId: string) => void;
  viewType?: 'team' | 'department' | 'businessunit' | 'all';
  currentUserId?: string;
}

// Calculate positions for hierarchical layout (top-down tree)
function calculatePositions(
  organogramNodes: OrganogramNode[],
  nodeClickHandler: (nodeId: string) => void,
  currentUserId?: string,
  startX: number = 0,
  startY: number = 0,
  levelWidth: number = 250,
  levelHeight: number = 150
): { nodes: Node[]; edges: Edge[]; maxX: number; maxY: number } {
  const flowNodes: Node[] = [];
  const flowEdges: Edge[] = [];
  let maxX = 0;
  let maxY = 0;

  function processNode(
    organogramNode: OrganogramNode,
    parentId: string | null,
    level: number,
    indexInLevel: number,
    totalInLevel: number,
    nodeClickHandler: (nodeId: string) => void,
    currentUserId?: string
  ) {
    const nodeId = organogramNode.id;
    const y = startY + level * levelHeight;
    
    // Center nodes horizontally within their level
    const levelWidthTotal = Math.max(totalInLevel * levelWidth, 400);
    const x = startX + (indexInLevel + 0.5) * (levelWidthTotal / totalInLevel) - 100;

    // Root nodes are those without a parent (level 0 and parentId is null)
    const isRootNode = parentId === null && level === 0;

    flowNodes.push({
      id: nodeId,
      type: 'organogramNode',
      position: { x, y },
      data: {
        node: organogramNode,
        onNodeClick: nodeClickHandler,
        isRootNode: isRootNode,
        currentUserId: currentUserId,
      },
    });

    maxX = Math.max(maxX, x + 220);
    maxY = Math.max(maxY, y + 100);

    // Process children
    if (organogramNode.children && organogramNode.children.length > 0) {
      organogramNode.children.forEach((child, childIndex) => {
        processNode(child, nodeId, level + 1, childIndex, organogramNode.children.length, nodeClickHandler, currentUserId);
        
        // Create edge from parent to child
        flowEdges.push({
          id: `${nodeId}-${child.id}`,
          source: nodeId,
          target: child.id,
          type: 'smoothstep',
          animated: false,
          style: { stroke: '#b1b1b7', strokeWidth: 2 },
        });
      });
    }
  }

  // Process all root nodes
  organogramNodes.forEach((rootNode, rootIndex) => {
    processNode(rootNode, null, 0, rootIndex, organogramNodes.length, nodeClickHandler, currentUserId);
  });

  return { nodes: flowNodes, edges: flowEdges, maxX, maxY };
}

function OrganogramFlowInner({ nodes, onNodeClick, viewType = 'team', currentUserId }: OrganogramFlowProps) {
  const { flowNodes, flowEdges } = useMemo(() => {
    const result = calculatePositions(nodes, onNodeClick, currentUserId);
    // Only highlight root nodes in "all" view
    if (viewType === 'all') {
      result.nodes = result.nodes.map(node => ({
        ...node,
        data: {
          ...node.data,
          isRootNode: node.data.isRootNode,
        },
      }));
    } else {
      // Clear isRootNode flag for other views
      result.nodes = result.nodes.map(node => ({
        ...node,
        data: {
          ...node.data,
          isRootNode: false,
        },
      }));
    }
    return { flowNodes: result.nodes, flowEdges: result.edges };
  }, [nodes, onNodeClick, viewType, currentUserId]);

  const [reactFlowNodes, setNodes, onNodesChange] = useNodesState(flowNodes);
  const [reactFlowEdges, setEdges, onEdgesChange] = useEdgesState(flowEdges);

  // Update nodes and edges when props change
  useMemo(() => {
    setNodes(flowNodes);
    setEdges(flowEdges);
  }, [flowNodes, flowEdges, setNodes, setEdges]);

  const onInit = useCallback((instance: ReactFlowInstance) => {
    // Fit view to show all nodes
    setTimeout(() => {
      instance.fitView({ padding: 0.2 });
    }, 100);
  }, []);

  const nodeTypes = useMemo(() => NODE_TYPES, []);
  const edgeTypes = useMemo(() => EDGE_TYPES, []);

  return (
    <ReactFlow
      nodes={reactFlowNodes}
      edges={reactFlowEdges}
      onNodesChange={onNodesChange}
      onEdgesChange={onEdgesChange}
      onInit={onInit}
      nodeTypes={nodeTypes}
      edgeTypes={edgeTypes}
      connectionMode={ConnectionMode.Loose}
      fitView
      attributionPosition="bottom-left"
    >
      <Background color="#f5f5f5" gap={16} />
      <Controls />
      <MiniMap
        nodeColor={() => {
          return '#667eea';
        }}
        maskColor="rgba(0, 0, 0, 0.1)"
      />
    </ReactFlow>
  );
}

export default function OrganogramFlow({ nodes, onNodeClick, viewType, currentUserId }: OrganogramFlowProps) {
  return (
    <div style={{ width: '100%', height: '600px', border: '1px solid #e0e0e0', borderRadius: '4px' }}>
      <ReactFlowProvider>
        <OrganogramFlowInner nodes={nodes} onNodeClick={onNodeClick} viewType={viewType} currentUserId={currentUserId} />
      </ReactFlowProvider>
    </div>
  );
}
